import {forwardRef, useCallback, useRef, type MouseEvent, type MutableRefObject, type PointerEvent, type ReactNode} from 'react';
import {useDockStyle} from './callDock';
import {useDockDrag} from './useDockDrag';

export type CallBubbleTone = 'call' | 'dialer';

interface CallBubbleProps {
    readonly tone: CallBubbleTone;
    readonly label: string;
    readonly urgent?: boolean;
    readonly testId?: string;
    readonly placement?: 'floating' | 'inline';
    readonly width?: number;
    readonly children: ReactNode;
}

const TONES: Record<CallBubbleTone, string> = {
    call: 'phq-call-ui border-[var(--phq-call-line)] bg-[var(--phq-call-canvas)] [background-image:var(--phq-call-stage-gradient)] p-4 phq-call-shadow-overlay',
    dialer: 'phq-call-light border-[var(--phq-call-line)] bg-[var(--phq-call-surface)] phq-call-shadow-overlay',
};

const DEFAULT_WIDTH = 320;

// A press on any of these keeps its own meaning; the bubble only moves when you grab its background.
const NOT_A_HANDLE = [
    'button', 'a', 'input', 'select', 'textarea', 'label', 'video',
    '[role="button"]', '[role="menu"]', '[role="menuitem"]', '[role="listbox"]', '[role="option"]', '[role="slider"]', '[contenteditable="true"]',
].join(', ');

export const CallBubble = forwardRef<HTMLElement, CallBubbleProps>(({tone, label, urgent = false, testId, placement = 'floating', width = DEFAULT_WIDTH, children}, ref) => {
    const docked = useDockStyle({width});
    const floating = placement === 'floating';
    // Call bubbles move like the Start a call pill; the dialer keeps its resize grip instead.
    const movable = floating && tone === 'call';
    const element = useRef<HTMLElement | null>(null);
    const {position, onPointerDown, onPointerMove, onPointerEnd, consumeClick} = useDockDrag(element);

    const setElement = useCallback((node: HTMLElement | null) => {
        element.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            (ref as MutableRefObject<HTMLElement | null>).current = node;
        }
    }, [ref]);

    const startDrag = (event: PointerEvent<HTMLElement>) => {
        if (!movable || (event.target as Element).closest(NOT_A_HANDLE)) {
            return;
        }
        // Keeps the press from selecting text while the bubble moves.
        event.preventDefault();
        onPointerDown(event);
    };

    const swallowDragClick = (event: MouseEvent<HTMLElement>) => {
        if (consumeClick()) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const style = !floating
        ? {width}
        : position
            ? {left: position.left, top: position.top, width, maxHeight: docked.maxHeight}
            : {...docked, width};

    return (
        <section
            ref={setElement}
            role={urgent ? 'alert' : 'region'}
            aria-live={urgent ? 'assertive' : 'polite'}
            aria-label={label}
            data-testid={testId ?? 'call-bubble'}
            data-tone={tone}
            data-dragging={position ? 'true' : undefined}
            style={style}
            onPointerDown={movable ? startDrag : undefined}
            onPointerMove={movable ? onPointerMove : undefined}
            onPointerUp={movable ? onPointerEnd : undefined}
            onPointerCancel={movable ? onPointerEnd : undefined}
            onClickCapture={movable ? swallowDragClick : undefined}
            className={`phq-call-enter ${floating ? 'fixed z-[60]' : 'relative'} flex flex-col overflow-hidden rounded-[20px] border ${TONES[tone]} ${movable ? (position ? 'cursor-grabbing select-none' : 'cursor-grab') : ''}`}
        >
            {children}
        </section>
    );
});

CallBubble.displayName = 'CallBubble';
